import React, { useState, useEffect } from 'react';
import { X, Monitor, LogOut, Trash2, CreditCard, MessageSquare, ChevronRight, Timer } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { submitFeedback } from './firestore';
import type { UserProgressMap, AppearanceMode } from './firestore';

const ORANGE = '#f78320';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  appearance: AppearanceMode;
  onAppearanceChange: (a: AppearanceMode) => void;
  onClearProgress: () => void;
  onLogOut: () => void;
  isSubscriber: boolean;
  userId: string;
  userEmail: string;
  sleepTimerMinutes: number | null;
  onSleepTimerChange: (minutes: number | null) => void;
  userProgress: UserProgressMap | null;
  tracks: Array<{ title: string; step: number | string }>;
}

type Section = 'progress' | 'settings' | 'account' | 'about' | null;

const SLEEP_OPTIONS: Array<{ label: string; value: number | null }> = [
  { label: 'Off',    value: null },
  { label: '30 min', value: 30  },
  { label: '60 min', value: 60  },
];

const APPEARANCE_OPTIONS: Array<{ label: string; value: AppearanceMode }> = [
  { label: 'System', value: 'system' },
  { label: 'Light',  value: 'light'  },
  { label: 'Dark',   value: 'dark'   },
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  darkMode,
  appearance,
  onAppearanceChange,
  onClearProgress,
  onLogOut,
  isSubscriber,
  userId,
  userEmail,
  sleepTimerMinutes,
  onSleepTimerChange,
  userProgress,
  tracks,
}) => {
  const [activeSection, setActiveSection] = useState<Section>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveSection(null);
      setConfirmClear(false);
      setFeedbackOpen(false);
      setFeedbackMessage('');
      setFeedbackSent(false);
    }
  }, [isOpen]);

  const toggleSection = (section: Section) => {
    setActiveSection(prev => prev === section ? null : section);
    setConfirmClear(false);
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const functions = getFunctions();
      const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const { data } = await createPortalLink({ returnUrl: window.location.origin }) as any;
      window.location.assign(data.url);
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      await submitFeedback(userId, userEmail, feedbackMessage.trim(), isSubscriber);
      setFeedbackSent(true);
      setFeedbackMessage('');
      setTimeout(() => { setFeedbackOpen(false); setFeedbackSent(false); }, 2000);
    } catch (err) {
      console.error('Feedback error:', err);
    } finally {
      setFeedbackSending(false);
    }
  };

  const hoverBg  = darkMode ? 'hover:bg-zinc-700' : 'hover:bg-stone-100';
  const divider  = <div className={`h-px ${darkMode ? 'bg-zinc-700' : 'bg-stone-200'}`} />;
  const panelBg  = darkMode ? 'bg-zinc-800 text-white' : 'bg-white text-stone-800';

  // Section header — inlined (NOT a sub-component) to avoid remount on every animation frame
  const sectionHeader = (label: string, section: Section) => (
    <button
      key={`hdr-${section}`}
      onClick={() => toggleSection(section)}
      className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm font-bold uppercase tracking-wider transition-colors ${hoverBg} ${
        activeSection === section ? (darkMode ? 'bg-zinc-700/60' : 'bg-stone-100') : ''
      }`}
    >
      <span>{label}</span>
      <ChevronRight
        size={16}
        className={`transition-transform duration-200 ${activeSection === section ? 'rotate-90' : ''} ${
          darkMode ? 'text-zinc-400' : 'text-stone-400'
        }`}
      />
    </button>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 z-50 shadow-xl transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${panelBg}`}
      >
        {/* Close button */}
        <div className={`flex items-center justify-end p-3 flex-shrink-0 border-b ${darkMode ? 'border-zinc-700' : 'border-stone-200'}`}>
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-stone-100'}`}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto flex flex-col">

          {/* ── Your Progress ── */}
          {sectionHeader('Your Progress', 'progress')}
          {activeSection === 'progress' && (() => {
            const countSteps = (step: string | number): number => {
              const str = step.toString();
              if (str.includes('-')) {
                const [a, b] = str.split('-').map(Number);
                return b - a + 1;
              }
              return 1;
            };
            const totalSteps = tracks.reduce((sum, t) => sum + countSteps(t.step), 0);
            const completedSteps = Object.entries(userProgress || {})
              .filter(([, data]) => data.completed)
              .reduce((sum, [step]) => sum + countSteps(step), 0);
            const totalPlays = Object.values(userProgress || {}).filter(t => t.completed).reduce((sum, t) => sum + (t.playCount ?? 0), 0);
            return (
              <div className="py-2 space-y-1">
                {/* Summary stats */}
                <div className={`mx-4 p-3 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-stone-100'}`}>
                  <div className={`flex justify-between text-sm mb-1 ${darkMode ? 'text-zinc-300' : 'text-stone-600'}`}>
                    <span>Steps Completed</span>
                    <span className="font-semibold">{completedSteps} / {totalSteps}</span>
                  </div>
                  <div className={`flex justify-between text-sm ${darkMode ? 'text-zinc-300' : 'text-stone-600'}`}>
                    <span>Total replays</span>
                    <span className="font-semibold">{totalPlays}</span>
                  </div>
                </div>

                {/* Per-track list */}
                <div className="mx-4 max-h-48 overflow-y-auto">
                  {tracks.map((track) => {
                    const stepKey = track.step.toString();
                    const trackData = userProgress?.[stepKey];
                    const playCount = trackData?.playCount ?? 0;
                    const pipsToShow = Math.min(playCount, 3);
                    const extraPlays = playCount > 3 ? playCount - 3 : 0;
                    return (
                      <div key={stepKey} className={`flex items-center justify-between py-1.5 text-sm ${darkMode ? 'text-zinc-300' : 'text-stone-600'}`}>
                        <span className="truncate flex-1 mr-2">{track.title}</span>
                        <div className="flex items-center flex-shrink-0">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <span
                              key={i}
                              style={{ color: i < pipsToShow ? ORANGE : (darkMode ? '#52525b' : '#d6d3d1') }}
                              className="text-sm leading-none"
                            >●</span>
                          ))}
                          {extraPlays > 0 && (
                            <span className={`text-xs ml-1 ${darkMode ? 'text-zinc-400' : 'text-stone-400'}`}>+{extraPlays}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Reset Progress */}
                {!confirmClear ? (
                  <button
                    onClick={() => setConfirmClear(true)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-base transition-colors ${hoverBg} text-red-500`}
                  >
                    <Trash2 size={18} />
                    <span>Reset Progress</span>
                  </button>
                ) : (
                  <div className={`mx-4 p-3 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-stone-100'}`}>
                    <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      Are you sure?
                    </p>
                    <p className={`text-sm mb-3 leading-snug ${darkMode ? 'text-zinc-300' : 'text-stone-600'}`}>
                      This will permanently reset your progress and cannot be undone. We recommend this if you're returning to Spanish after a long break or want to start fresh.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => { onClearProgress(); setConfirmClear(false); }}
                        className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                      >
                        Yes, Reset
                      </button>
                      <button
                        onClick={() => setConfirmClear(false)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                          darkMode ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : 'bg-stone-200 hover:bg-stone-300 text-stone-700'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {divider}

          {/* ── Settings ── */}
          {sectionHeader('Settings', 'settings')}
          {activeSection === 'settings' && (
            <div className="py-2 space-y-1">
              {/* Appearance */}
              <div className={`mx-4 p-3 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-stone-100'}`}>
                <div className={`flex items-center space-x-2 mb-2 text-sm font-medium ${darkMode ? 'text-zinc-300' : 'text-stone-500'}`}>
                  <Monitor size={15} />
                  <span>Appearance</span>
                </div>
                <div className="flex space-x-2">
                  {APPEARANCE_OPTIONS.map(({ label, value }) => (
                    <button
                      key={label}
                      onClick={() => onAppearanceChange(value)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        appearance === value
                          ? 'text-white'
                          : darkMode
                          ? 'bg-zinc-600 text-zinc-200 hover:bg-zinc-500'
                          : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                      }`}
                      style={appearance === value ? { backgroundColor: ORANGE } : undefined}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sleep Timer */}
              <div className={`mx-4 p-3 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-stone-100'}`}>
                <div className={`flex items-center space-x-2 mb-2 text-sm font-medium ${darkMode ? 'text-zinc-300' : 'text-stone-500'}`}>
                  <Timer size={15} />
                  <span>Sleep Timer</span>
                </div>
                <div className="flex space-x-2">
                  {SLEEP_OPTIONS.map(({ label, value }) => (
                    <button
                      key={label}
                      onClick={() => onSleepTimerChange(value)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        sleepTimerMinutes === value
                          ? 'text-white'
                          : darkMode
                          ? 'bg-zinc-600 text-zinc-200 hover:bg-zinc-500'
                          : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                      }`}
                      style={sleepTimerMinutes === value ? { backgroundColor: ORANGE } : undefined}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reload App */}
              <button
                onClick={() => window.location.reload()}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-base transition-colors ${hoverBg}`}
              >
                <span className={darkMode ? 'text-zinc-300' : 'text-stone-600'}>Reload App</span>
              </button>
            </div>
          )}

          {divider}

          {/* ── Account ── */}
          {sectionHeader('Account', 'account')}
          {activeSection === 'account' && (
            <div className="py-2 space-y-1">
              <div className={`mx-4 px-3 py-3 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-stone-100'}`}>
                <p className={`text-sm mb-0.5 ${darkMode ? 'text-zinc-400' : 'text-stone-500'}`}>Signed in as</p>
                <p className="text-base font-medium truncate">{userEmail}</p>
              </div>

              <button
                onClick={onLogOut}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-base transition-colors ${hoverBg} ${darkMode ? 'text-red-400' : 'text-red-500'}`}
              >
                <LogOut size={18} />
                <span>Log Out</span>
              </button>

              {isSubscriber && (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-base transition-colors ${hoverBg} ${portalLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <CreditCard size={18} />
                  <span>{portalLoading ? 'Loading...' : 'Manage Account'}</span>
                </button>
              )}
            </div>
          )}

          {divider}

          {/* ── About ── */}
          {sectionHeader('About', 'about')}
          {activeSection === 'about' && (
            <div className="px-4 py-3 space-y-1">
              <p className="text-base font-semibold">Bilingual Books, Inc.</p>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-zinc-400' : 'text-stone-500'}`}>
                1900 W. Nickerson Street, Suite 116-240<br />Seattle, WA 98119
              </p>
              <div className={`space-y-1 pt-1 text-sm ${darkMode ? 'text-zinc-400' : 'text-stone-500'}`}>
                <p>(206) 284-4211</p>
                <a href="mailto:info@bbks.com" className={darkMode ? 'block text-indigo-400 hover:text-indigo-300' : 'block text-indigo-600 hover:text-indigo-500'}>
                  info@bbks.com
                </a>
                <a href="https://www.bbks.com" target="_blank" rel="noopener noreferrer" className={darkMode ? 'block text-indigo-400 hover:text-indigo-300' : 'block text-indigo-600 hover:text-indigo-500'}>
                  BBKS.com
                </a>
              </div>
            </div>
          )}

          {divider}

          {/* Book promo — always visible, pushed to bottom of scroll area */}
          <div className="mt-auto flex flex-col items-center px-6 pt-5 pb-4 text-center">
            <img
              src="/images/Spanish_book.png"
              alt="SPANISH in 10 minutes a day"
              className="w-36 shadow-lg rounded mb-3"
            />
            <p className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-stone-800'}`}>
              Looking for the book?
            </p>
            <a
              href="https://www.bbks.com/spanish/book.html"
              target="_blank"
              rel="noopener noreferrer"
              className={`text-sm ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
            >
              Available at BBKS.com →
            </a>
          </div>
        </div>

        {/* Footer — always pinned to bottom */}
        <div className={`flex-shrink-0 border-t ${darkMode ? 'border-zinc-700' : 'border-stone-200'}`}>

          {/* Send Feedback */}
          {!feedbackOpen ? (
            <button
              onClick={() => setFeedbackOpen(true)}
              className="w-full flex items-center space-x-3 px-4 py-3 text-base text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: ORANGE }}
            >
              <MessageSquare size={18} />
              <span>Send Feedback</span>
            </button>
          ) : feedbackSent ? (
            <div className={`px-4 py-3 text-base text-center ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              Thank you for your feedback!
            </div>
          ) : (
            <div className="px-4 py-3 space-y-2">
              <textarea
                value={feedbackMessage}
                onChange={e => setFeedbackMessage(e.target.value)}
                placeholder="Share what's working, what's not, or anything that felt confusing."
                rows={3}
                className={`w-full p-2 rounded text-sm resize-none ${
                  darkMode
                    ? 'bg-zinc-700 text-white placeholder-zinc-400 border border-zinc-600'
                    : 'bg-stone-100 text-stone-800 placeholder-stone-400 border border-stone-200'
                }`}
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSendFeedback}
                  disabled={feedbackSending || !feedbackMessage.trim()}
                  className="flex-1 px-3 py-2 text-white rounded text-sm font-medium hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: ORANGE }}
                >
                  {feedbackSending ? 'Sending...' : 'Send'}
                </button>
                <button
                  onClick={() => { setFeedbackOpen(false); setFeedbackMessage(''); }}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-stone-200 hover:bg-stone-300 text-stone-700'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Copyright + version */}
          <div className={`flex justify-between items-center px-4 py-2 border-t text-xs ${
            darkMode ? 'border-zinc-700 text-zinc-500' : 'border-stone-200 text-stone-500'
          }`}>
            <span>© 2026 Bilingual Books, Inc.</span>
            <span>v0.1.0</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
